import { RelayablyWorkspaceDocument } from '@monorepo/relayably/workspaces'
import { Platform } from 'react-native'
import { buildRelayablyInternalUrl } from '../api/relayablyInternalApi'
import { relayablyApi } from '../auth/relayablyApi'
import { mockRelayablyWorkspaceApi } from './mockRelayablyWorkspaceApi'
import {
  RelayablyCancelSubscriptionResponse,
  RelayablyCheckoutResponse,
  RelayablyCheckoutStatusResponse,
  RelayablyPaymentsProductsResponse,
  RelayablyPortalResponse,
  WorkspaceResponse,
  WorkspaceVoiceSearchFilters,
  WorkspaceVoiceSearchResponse,
} from './types'

const request = async <T>(path: string, sessionToken: string, options: RequestInit = {}): Promise<T> => {
  const response = await fetch(buildRelayablyInternalUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-relayably-client': 'relayably-app',
      Authorization: `Bearer ${sessionToken}`,
      ...(options.headers ?? {}),
    },
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: { message?: string } } | null
    throw new Error(body?.error?.message ?? 'Workspace setup failed.')
  }

  return (await response.json()) as T
}

const withFallback = async <T>(networkCall: () => Promise<T>, fallback: () => Promise<T>) => {
  if ((process.env.EXPO_PUBLIC_RELAYABLY_RUNTIME_MODE?.toLowerCase() ?? 'development') !== 'development') {
    return networkCall()
  }

  try {
    return await networkCall()
  } catch (error) {
    if (error instanceof TypeError) {
      return fallback()
    }

    const message = error instanceof Error ? error.message : ''
    if (message.includes('Network request failed') || message.includes('fetch')) {
      return fallback()
    }

    throw error
  }
}

const resolveReturnUrl = (path = '/settings') => {
  if (Platform.OS === 'web' && typeof globalThis.location !== 'undefined') {
    return `${globalThis.location.origin}${path}`
  }

  return undefined
}

export const relayablyWorkspaceApi = {
  async updateUserProfile(
    sessionToken: string,
    profile: {
      fullName?: string
      country?: string
      timezone?: string
      phoneNumber?: string
    }
  ) {
    return request<{ profile: { fullName: string; email: string; country: string; timezone: string } }>(
      '/internal/users/profile',
      sessionToken,
      {
        method: 'PATCH',
        body: JSON.stringify(profile),
      }
    )
  },
  async createCheckoutSession(
    sessionToken: string,
    planId: string,
    options?: {
      cancelUrl?: string
      successUrl?: string
    }
  ): Promise<RelayablyCheckoutResponse> {
    return request<RelayablyCheckoutResponse>('/internal/payments/checkout', sessionToken, {
      method: 'POST',
      body: JSON.stringify({
        planId,
        cancelUrl: options?.cancelUrl ?? resolveReturnUrl('/settings'),
        successUrl: options?.successUrl ?? resolveReturnUrl('/settings'),
      }),
    })
  },
  async createPortalSession(sessionToken: string): Promise<RelayablyPortalResponse> {
    return request<RelayablyPortalResponse>('/internal/payments/portal', sessionToken, {
      method: 'POST',
      body: JSON.stringify({
        returnUrl: resolveReturnUrl('/settings'),
      }),
    })
  },
  async cancelSubscription(sessionToken: string): Promise<RelayablyCancelSubscriptionResponse> {
    return request<RelayablyCancelSubscriptionResponse>('/internal/payments/subscription/cancel', sessionToken, {
      method: 'POST',
    })
  },
  async listVoices(sessionToken: string, filters: WorkspaceVoiceSearchFilters = {}): Promise<WorkspaceVoiceSearchResponse> {
    const searchParams = new URLSearchParams()

    if (filters.accent?.trim()) searchParams.set('accent', filters.accent.trim())
    if (filters.gender) searchParams.set('gender', filters.gender)
    if (filters.age) searchParams.set('age', filters.age)
    if (filters.pageToken) searchParams.set('pageToken', filters.pageToken)
    if (filters.pageSize) searchParams.set('pageSize', String(filters.pageSize))

    const suffix = searchParams.toString()
    return request<WorkspaceVoiceSearchResponse>(`/internal/voices${suffix ? `?${suffix}` : ''}`, sessionToken)
  },
  async listPaymentProducts(sessionToken: string): Promise<RelayablyPaymentsProductsResponse> {
    return request<RelayablyPaymentsProductsResponse>('/internal/payments/products', sessionToken)
  },
  async describeBusiness(sessionToken: string, input: { businessName?: string; website?: string }): Promise<{ description: string }> {
    return request<{ description: string }>('/internal/workspace/describe', sessionToken, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },
  async getCheckoutSession(sessionToken: string, checkoutSessionId: string): Promise<RelayablyCheckoutStatusResponse> {
    return request<RelayablyCheckoutStatusResponse>(`/internal/payments/checkout/${checkoutSessionId}`, sessionToken)
  },
  async getCurrentWorkspace(sessionToken: string, profile: { fullName: string; email: string }): Promise<WorkspaceResponse> {
    return withFallback(
      () => request<WorkspaceResponse>('/internal/workspaces/current', sessionToken),
      () => mockRelayablyWorkspaceApi.getCurrentWorkspace(sessionToken, profile)
    )
  },
  async saveWorkspace(sessionToken: string, workspace: RelayablyWorkspaceDocument): Promise<WorkspaceResponse> {
    return withFallback(
      async () => {
        await this.updateUserProfile(sessionToken, workspace.profile)

        return request<WorkspaceResponse>('/internal/workspaces/current', sessionToken, {
          method: 'PATCH',
          body: JSON.stringify(workspace),
        })
      },
      () => mockRelayablyWorkspaceApi.saveWorkspace(sessionToken, workspace)
    )
  },
  async completeWorkspace(sessionToken: string, workspace: RelayablyWorkspaceDocument): Promise<WorkspaceResponse> {
    await this.updateUserProfile(sessionToken, workspace.profile)

    return request<WorkspaceResponse>('/internal/workspaces/current/complete', sessionToken, {
      method: 'POST',
      body: JSON.stringify(workspace),
    })
  },

  async refreshSession(sessionToken: string) {
    return relayablyApi.getMe(sessionToken)
  },
}
